/* assets/bundle-holster-selector.js
   ------------------------------------------------------------------
   Drives every <div data-bhs-selector> on the page (Velcro + IWB on the
   Expandable CCW Bag Setup bundle).

   The hidden property inputs are rendered in Liquid with
   form="{{ product_form_id }}", so this script does NOT create or inject
   anything into the form — it just keeps those inputs in sync with the
   dropdown selections.

   Uses the same firearm manifest as firearm-options.js
   (window.nffi.firearmManifestJsonUrl). The manifest + brands file are
   fetched ONCE and shared; each selector loads its own models file
   (manifest.models[modelsKey]). Data is kept in local closures so the two
   selectors stay independent and window.nffi is left untouched.
   ------------------------------------------------------------------ */
(function () {
  "use strict";

  document.addEventListener("DOMContentLoaded", init);

  function init() {
    var instances = Array.prototype.slice.call(
      document.querySelectorAll("[data-bhs-selector]")
    );
    if (!instances.length) return;

    if (!window.nffi || !window.nffi.firearmManifestJsonUrl) {
      console.error(
        "[bundle-holster-selector] window.nffi.firearmManifestJsonUrl is missing."
      );
      instances.forEach(showInstanceError);
      return;
    }

    // ---- shared, page-scoped caches ----
    var manifestPromise = null;
    var brandsPromise = null;
    var modelsPromiseByKey = {};

    function getManifest() {
      if (!manifestPromise) {
        manifestPromise = fetchJson(
          window.nffi.firearmManifestJsonUrl,
          "firearm manifest"
        ).then(function (manifest) {
          validateManifest(manifest);
          return manifest;
        });
      }
      return manifestPromise;
    }

    function getBrands() {
      if (!brandsPromise) {
        brandsPromise = getManifest().then(function (manifest) {
          return fetchJsonWithFallback(
            manifest.brands.currentUrl,
            manifest.brands.fallbackUrl,
            "firearm brands"
          );
        });
      }
      return brandsPromise;
    }

    function getModels(modelsKey) {
      if (!modelsPromiseByKey[modelsKey]) {
        modelsPromiseByKey[modelsKey] = getManifest().then(function (manifest) {
          var entry = manifest.models && manifest.models[modelsKey];
          if (!entry || (!entry.currentUrl && !entry.fallbackUrl)) {
            throw new Error(
              '[bundle-holster-selector] manifest.models["' +
                modelsKey +
                '"] has no usable URL.'
            );
          }
          return fetchJsonWithFallback(
            entry.currentUrl,
            entry.fallbackUrl,
            "firearm models (" + modelsKey + ")"
          );
        });
      }
      return modelsPromiseByKey[modelsKey];
    }

    instances.forEach(function (el) {
      setupInstance(el, { getBrands: getBrands, getModels: getModels });
    });
  }

  function setupInstance(root, ctx) {
    var sid = root.getAttribute("data-selector-id") || "sel";
    var modelsKey = root.getAttribute("data-models-key");

    var handSelect = root.querySelector('[data-role="hand"]');
    var brandSelect = root.querySelector('[data-role="brand"]');
    var modelSelect = root.querySelector('[data-role="model"]');

    // Hidden property inputs rendered by Liquid (form-associated).
    var props = {
      hand: root.querySelector('[data-prop="hand"]'),
      brand: root.querySelector('[data-prop="brand"]'),
      model: root.querySelector('[data-prop="model"]'),
      modelSku: root.querySelector('[data-prop="modelSku"]')
    };

    var instanceBrands = [];
    var instanceModels = [];

    // Hand has a default ("Right Hand") with no change event — write it now.
    if (props.hand && handSelect) props.hand.value = selectedText(handSelect);
    initDropdownSelectedLabels(root);

    // ---- load data ----
    Promise.all([ctx.getBrands(), ctx.getModels(modelsKey)])
      .then(function (results) {
        instanceBrands = results[0] || [];
        instanceModels = results[1] || [];

        populateBrands(brandSelect, instanceBrands, instanceModels);
        initDropdownSelectedLabels(root);
      })
      .catch(function (err) {
        console.error(
          "[bundle-holster-selector] data load failed for '" + sid + "':",
          err
        );
        showInstanceError(root);
      });

    // ---- events ----
    if (handSelect) {
      handSelect.addEventListener("change", function () {
        if (props.hand) props.hand.value = selectedText(handSelect);
        initDropdownSelectedLabels(root);
      });
    }

    if (brandSelect) {
      brandSelect.addEventListener("change", function () {
        resetModelSelect(modelSelect);
        if (props.model) props.model.value = "";
        if (props.modelSku) props.modelSku.value = "";

        if (this.value) {
          populateModels(modelSelect, instanceModels, this.value);
        }
        if (props.brand) props.brand.value = selectedText(brandSelect);
        initDropdownSelectedLabels(root);
      });
    }

    if (modelSelect) {
      modelSelect.addEventListener("change", function () {
        if (props.model) props.model.value = selectedText(modelSelect);   // readable name
        if (props.modelSku) props.modelSku.value = modelSelect.value || ""; // SKU (hidden)
        initDropdownSelectedLabels(root);
      });
    }
  }

  /* ---------------- DOM helpers ---------------- */

  function populateBrands(brandSelect, brands, models) {
    if (!brandSelect) return;
    resetSelect(brandSelect, "Select Brand");

    // Only show brands that actually have models in THIS instance's set.
    var brandIdsWithModels = {};
    (models || []).forEach(function (m) {
      brandIdsWithModels[String(m.brandId)] = true;
    });

    (brands || [])
      .filter(function (b) {
        return brandIdsWithModels[String(b.id)];
      })
      .forEach(function (b) {
        var opt = document.createElement("option");
        opt.value = b.id;
        opt.textContent = b.name;
        brandSelect.appendChild(opt);
      });
  }

  function populateModels(modelSelect, models, brandId) {
    if (!modelSelect) return;
    resetSelect(modelSelect, "Select Model");

    var filtered = (models || []).filter(function (m) {
      return String(m.brandId) === String(brandId);
    });

    filtered.forEach(function (m) {
      var opt = document.createElement("option");
      opt.value = m.sku; // SKU in value (matches firearm-options.js)
      opt.textContent = m.name;
      modelSelect.appendChild(opt);
    });

    modelSelect.disabled = filtered.length === 0;
  }

  function resetModelSelect(modelSelect) {
    if (!modelSelect) return;
    resetSelect(modelSelect, "Select Model");
    modelSelect.disabled = true;
  }

  function resetSelect(select, placeholder) {
    select.innerHTML =
      '<option value="" disabled selected>' + placeholder + "</option>";
  }

  function selectedText(select) {
    if (!select) return "";
    var opt = select.options[select.selectedIndex];
    if (!opt || opt.disabled) return "";
    return (opt.textContent || "").trim();
  }

  function showInstanceError(root) {
    var el = root.querySelector("[data-bhs-error]");
    if (!el) {
      el = document.createElement("div");
      el.setAttribute("data-bhs-error", "");
      el.className = "validation-error";
      el.style.position = "static";
      el.style.marginTop = "8px";
      root.appendChild(el);
    }
    el.textContent =
      "Firearm options are temporarily unavailable. Please refresh and try again.";
    el.style.display = "inline-block";
  }

  // Mirrors firearm-options.js: label reads "Firearm Brand: Glock" once chosen.
  function initDropdownSelectedLabels(scope) {
    var wraps = Array.prototype.slice.call(
      scope.querySelectorAll(".firearm-dropdowns")
    );
    wraps.forEach(function (wrap) {
      var label = wrap.querySelector(".dropdown-label");
      var select = wrap.querySelector("select.dropdown-select");
      if (!label || !select) return;

      if (!label.dataset.baseText) {
        label.dataset.baseText = (label.textContent || "")
          .trim()
          .replace(/:.*$/, "");
      }
      var baseText = label.dataset.baseText;
      var opt = select.options[select.selectedIndex];
      var txt = ((opt && opt.textContent) || "").trim();
      var isPlaceholder =
        !select.value || (opt && opt.disabled) || /^select\b/i.test(txt);
      label.textContent = isPlaceholder ? baseText : baseText + ": " + txt;
    });
  }

  /* ---------------- fetch helpers ---------------- */

  function fetchJson(url, label) {
    if (!url) return Promise.reject(new Error(label + ": URL is empty."));
    return fetch(url, { cache: "no-store" }).then(function (res) {
      if (!res.ok) throw new Error(label + ": HTTP " + res.status);
      return res.json();
    });
  }

  function fetchJsonWithFallback(currentUrl, fallbackUrl, label) {
    return fetchJson(currentUrl, label + " (current)").catch(function (err) {
      console.warn(label + ": current failed, trying fallback.", err);
      if (!fallbackUrl) throw err;
      return fetchJson(fallbackUrl, label + " (fallback)");
    });
  }

  function validateManifest(manifest) {
    if (!manifest || typeof manifest !== "object")
      throw new Error("Firearm manifest is missing or invalid.");
    if (!manifest.brands || !manifest.models)
      throw new Error("Firearm manifest missing brands/models sections.");
    if (!manifest.brands.currentUrl && !manifest.brands.fallbackUrl)
      throw new Error("Firearm brands manifest entry has no usable URL.");
  }
})();